<template>
  <Listbox
    :model-value="modelValue"
    @update:model-value="$emit('update:modelValue', $event)"
    :disabled="disabled"
    multiple
    as="div"
    class="relative"
  >
    <div class="relative inline-block" :title="disabled && disabledReason ? disabledReason : undefined">
      <ListboxButton
        :id="id"
        class="inline-flex items-center gap-2 rounded-lg border px-3 py-1.5 text-sm font-medium shadow-sm transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
        :class="[
          disabled
            ? 'cursor-not-allowed opacity-50 border-gray-200 bg-gray-100 text-gray-400 dark:border-white/5 dark:bg-gray-800/50 dark:text-gray-500'
            : buttonClasses,
        ]"
      >
        <FunnelIcon class="size-4 shrink-0" aria-hidden="true" />
        <span>{{ label }}</span>
        <span
          v-if="modelValue.length > 0"
          class="inline-flex items-center justify-center rounded-full px-1.5 py-0.5 text-[10px] font-bold leading-none min-w-[18px] bg-indigo-600 text-white"
        >
          {{ modelValue.length }}
        </span>
        <ChevronDownIcon class="size-4 shrink-0" :class="disabled ? 'text-gray-300 dark:text-gray-600' : 'text-gray-400 dark:text-gray-500'" aria-hidden="true" />
      </ListboxButton>
    </div>

    <transition
      enter-active-class="transition ease-out duration-100"
      enter-from-class="opacity-0 scale-95"
      enter-to-class="opacity-100 scale-100"
      leave-active-class="transition ease-in duration-75"
      leave-from-class="opacity-100 scale-100"
      leave-to-class="opacity-0 scale-95"
    >
      <ListboxOptions
        class="absolute z-20 mt-1 max-h-60 w-56 overflow-auto rounded-lg border border-gray-200 bg-white py-1 text-sm shadow-lg focus:outline-none dark:border-white/10 dark:bg-gray-800"
      >
        <ListboxOption
          v-for="option in options"
          :key="option.value"
          :value="option.value"
          v-slot="{ selected }"
          as="template"
        >
          <li
            class="relative flex cursor-pointer select-none items-center gap-2.5 px-3 py-2 transition-colors hover:bg-gray-50 dark:hover:bg-white/5"
            :class="selected ? 'text-indigo-600 dark:text-indigo-400' : 'text-gray-700 dark:text-gray-300'"
          >
            <!-- Checkbox indicator -->
            <span
              class="flex size-4 shrink-0 items-center justify-center rounded border transition-colors"
              :class="selected
                ? 'border-indigo-600 bg-indigo-600 dark:border-indigo-500 dark:bg-indigo-500'
                : 'border-gray-300 bg-white dark:border-gray-600 dark:bg-gray-700'"
            >
              <CheckIcon
                v-if="selected"
                class="size-3 text-white"
                aria-hidden="true"
              />
            </span>
            <span class="truncate" :class="selected ? 'font-semibold' : 'font-normal'">
              {{ option.label }}
            </span>
          </li>
        </ListboxOption>

        <!-- Empty state -->
        <li
          v-if="options.length === 0"
          class="px-3 py-2 text-gray-400 dark:text-gray-500 text-center"
        >
          No options
        </li>
      </ListboxOptions>
    </transition>
  </Listbox>
</template>

<script setup lang="ts">
import {computed} from 'vue';
import {Listbox, ListboxButton, ListboxOption, ListboxOptions} from '@headlessui/vue';
import {CheckIcon, ChevronDownIcon, FunnelIcon} from '@heroicons/vue/20/solid';

interface FilterOption {
  value: string;
  label: string;
}

defineProps<{
  id?: string;
  label: string;
  options: FilterOption[];
  modelValue: string[];
  disabled?: boolean;
  disabledReason?: string;
}>();

defineEmits<{
  (e: 'update:modelValue', value: string[]): void;
}>();

const buttonClasses = computed(() => {
  // Slightly different styling when filter is active vs inactive
  return 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50 dark:border-white/10 dark:bg-gray-800 dark:text-gray-100 dark:hover:bg-gray-700';
});
</script>
